import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin,
    Modal,
    Form,
    Select,
    Drawer,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import CommonObj from "../../CommonObj";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { exportToExcel } from "../exporttoexcel/exporttoexcel";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";
import Html5QrcodePlugin from "../qrscanner/qrscanner";
import { Html5QrcodeScanType } from "html5-qrcode";
import { useAuth } from "../../auth/AuthContext";

import regexService, {
    removeSpaces,
} from "../../components/commonregex/regexService";

const MrbQueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchFilters, setsearchFilters] = useState({});
    const [selectedAction, setSelectedAction] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [exceptionActionStatuses, setExceptionActionStatuses] = useState([]);
    const [activeScanner, setActiveScanner] = useState(null);
    const [decodedResults, setDecodedResults] = useState([]);
    const { accessToken } = useAuthToken();
    const [isDisabled, setIsDisabled] = useState(false);
    const [operatorOptions, setOperatorOptions] = useState([]);
    const [selectedOpeId, setSelectedOpeId] = useState(null);
    const { roleUserId } = useAuth();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [rackAndShelfVal, setRackAndShelfVal] = useState("");
    const [errors, setErrors] = useState({});
    const [regexLoaded, setRegexLoaded] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [form] = Form.useForm();
    const searchInput = useRef(null);
    useEffect(() => {
        fetchExceptionActionStatuses();
    }, []);

    useEffect(() => {
        const uniqueResults = filterUniqueScans(decodedResults);

        if (uniqueResults.length > 0 && activeScanner) {
            const latestResult = uniqueResults[uniqueResults.length - 1];
            const scannedValue = latestResult.decodedText;

            switch (activeScanner) {
                case "Rack & Shelf ID":
                    setRackAndShelfVal(scannedValue);
                    form.setFieldsValue({ rackAndShelf: scannedValue });
                    form.validateFields(["rackAndShelf"]).catch(() => { });
                    break;
                default:
                    break;
            }
            setActiveScanner(null);
            setDecodedResults([]);
        }
    }, [decodedResults, activeScanner]);

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };

    const onNewScanResult = (decodedText, decodedResult) => {
        setDecodedResults((prev) => [...prev, decodedResult]);
    };
    function filterUniqueScans(results) {
        const filteredResults = [];
        if (results.length === 0) {
            return filteredResults;
        }
        filteredResults.push(results[0]);
        for (let i = 1; i < results.length; ++i) {
            if (results[i].decodedText !== results[i - 1].decodedText) {
                filteredResults.push(results[i]);
            }
        }
        return filteredResults;
    }
    const fetchReportFilter = async (isbn, defaultVendorId = null) => {
        setLoading(true);
        try {
            const payload = {
                isbn,
            };
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetVendorDetailsByISBN, {
                method: "POST",
                headers: myHeaders,
                mode: "cors",
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const result = await response.json();
            const vendors = Array.isArray(result) ? result : [result];

            setOperatorOptions(vendors);

            const matchedVendorId =
                defaultVendorId != null &&
                    vendors.some(
                        (option) => Number(option.vendorID) === Number(defaultVendorId),
                    )
                    ? String(defaultVendorId)
                    : null;

            setSelectedOpeId(matchedVendorId);
        } catch (err) {
            console.log("Fetch rejection data error:", err);
        } finally {
            setLoading(false);
        }
    };
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            handleReset(dataIndex, confirm, clearFilters);
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sorter: false,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const columns = [
        {
            title: "BID",
            dataIndex: "bid",
            key: "bid",
            width: 160,
        },
        {
            title: "ISBN",
            dataIndex: "isbn",
            key: "isbn",
            width: 200,
            ...getColumnSearchProps("isbn"),
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 220,
        },
        {
            title: "Author",
            dataIndex: "author",
            key: "author",
            width: 220,
        },
        {
            title: "Vendor",
            dataIndex: "vendor",
            key: "vendor",
            width: 220,
        },
        {
            title: "Rack and Shelf",
            dataIndex: "rackandShelf",
            key: "rackandShelf",
            width: 200,
        },
        {
            title: "Scanned Date",
            dataIndex: "scannedDate",
            key: "scannedDate",
            width: 200,
        },
        {
            title: "Operator ID",
            dataIndex: "operatorID",
            key: "operatorID",
            width: 200,
        },
        {
            title: "Review",
            dataIndex: "inReview",
            key: "inReview",
            width: 200,
            render: (text, record) =>
                record?.inReview ? <div>In Review</div> : <div></div>,
        },
        {
            title: "Action",
            dataIndex: "bid",
            key: "actions",
            width: 200,
            render: (text, record) => (
                <div>
                    <Space size="middle" title="Action">
                        <button
                            type="button"
                            data-track="ExceptionBookAction-open-recover-slide"
                            onClick={() => {
                                handleDrawerVisible(record);
                            }}
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clipPath="url(#clip0_5244_39974)">
                                    <path
                                        d="M5.53317 8.72222H4.93317V8.72222L5.53317 8.72222ZM5.53317 9.41667L5.08399 9.81445C5.19222 9.93667 5.34557 10.0095 5.50868 10.0162C5.6718 10.0228 5.83058 9.96274 5.94842 9.84976L5.53317 9.41667ZM7.16524 8.6831C7.40444 8.45376 7.41243 8.07395 7.1831 7.83476C6.95376 7.59556 6.57395 7.58757 6.33476 7.8169L6.75 8.25L7.16524 8.6831ZM4.94918 7.85221C4.72949 7.60414 4.35029 7.58113 4.10221 7.80082C3.85414 8.02051 3.83113 8.39971 4.05082 8.64779L4.5 8.25L4.94918 7.85221ZM11.1712 6.78244C11.3963 7.02569 11.7759 7.04047 12.0191 6.81544C12.2624 6.59042 12.2771 6.21081 12.0521 5.96756L11.6117 6.375L11.1712 6.78244ZM9.03278 5.25V4.65C6.77308 4.65 4.93317 6.46876 4.93317 8.72222H5.53317H6.13317C6.13317 7.14037 7.42692 5.85 9.03278 5.85V5.25ZM5.53317 8.72222L4.93317 8.72222L4.93317 9.41667H5.53317H6.13317L6.13317 8.72222L5.53317 8.72222ZM5.53317 9.41667L5.94842 9.84976L7.16524 8.6831L6.75 8.25L6.33476 7.8169L5.11793 8.98357L5.53317 9.41667ZM5.53317 9.41667L5.98236 9.01888L4.94918 7.85221L4.5 8.25L4.05082 8.64779L5.08399 9.81445L5.53317 9.41667ZM11.6117 6.375L12.0521 5.96756C11.3031 5.15782 10.2267 4.65 9.03278 4.65V5.25V5.85C9.88006 5.85 10.6409 6.20913 11.1712 6.78244L11.6117 6.375Z"
                                        fill="#002449"
                                    />
                                    <path
                                        d="M12.4641 8.58301L12.9126 8.18451C12.8043 8.06252 12.6509 7.98994 12.4878 7.98348C12.3248 7.97702 12.1662 8.03724 12.0485 8.15026L12.4641 8.58301ZM10.8344 9.31628C10.5954 9.54581 10.5877 9.92564 10.8173 10.1646C11.0468 10.4036 11.4266 10.4113 11.6656 10.1818L11.25 9.74902L10.8344 9.31628ZM13.0515 10.1475C13.2715 10.3953 13.6508 10.4177 13.8985 10.1976C14.1462 9.97749 14.1686 9.59825 13.9485 9.35052L13.5 9.74902L13.0515 10.1475ZM6.8125 11.2276C6.58574 10.986 6.20603 10.974 5.9644 11.2007C5.72278 11.4275 5.71073 11.8072 5.9375 12.0488L6.375 11.6382L6.8125 11.2276ZM8.9511 12.7497V13.3497C11.216 13.3497 13.0641 11.5331 13.0641 9.27745H12.4641H11.8641C11.8641 10.8572 10.5665 12.1497 8.9511 12.1497V12.7497ZM12.4641 9.27745H13.0641V8.58301H12.4641H11.8641V9.27745H12.4641ZM12.4641 8.58301L12.0485 8.15026L10.8344 9.31628L11.25 9.74902L11.6656 10.1818L12.8797 9.01575L12.4641 8.58301ZM12.4641 8.58301L12.0155 8.98151L13.0515 10.1475L13.5 9.74902L13.9485 9.35052L12.9126 8.18451L12.4641 8.58301ZM6.375 11.6382L5.9375 12.0488C6.68857 12.8491 7.76191 13.3497 8.9511 13.3497V12.7497V12.1497C8.10468 12.1497 7.3446 11.7946 6.8125 11.2276L6.375 11.6382Z"
                                        fill="#002449"
                                    />
                                    <circle
                                        opacity="0.5"
                                        cx="9"
                                        cy="9"
                                        r="7.5"
                                        stroke="#002449"
                                        strokeWidth="1.2"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_5244_39974">
                                        <rect width="18" height="18" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    </Space>
                </div>
            ),
        },
    ];

    const handleDrawerVisible = async (record) => {
        setIsDisabled(true);
        setTimeout(() => {
            setIsDisabled(false);
        }, 2000);
        if (!data || data.length === 0) {
            message.error(
                "Please add at least one item to the table before proceeding",
            );
            return;
        }
        form.resetFields();
        setSelectedRecord(record);
        setSelectedOpeId(
            record?.vendorId != null &&
                String(record.vendorId).trim() !== ""
                ? String(record.vendorId)
                : null,
        );
        setDrawerVisible(true);
        fetchReportFilter(record.isbn, record?.vendorId);
    };
    const handleDrawerClose = () => {
        setDrawerVisible(false);
        setSelectedAction(null);
        setSelectedOpeId(null);
        setSelectedRecord(null);
        setRackAndShelfVal("");
        setErrors({});
        form.resetFields();
    };

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetMRBQueueDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });
            const responseText = await response?.text();
            let result;

            if (!response.ok) {
                if (responseText?.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Network response was not ok");
                }
            }

            try {
                result = JSON.parse(responseText);
                result = responseText.includes("No books found.") ? [] : result;
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.referenceId,
            }));
            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;

            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    }, []);

    useEffect(() => {
        const loadRegexPatterns = async () => {
            try {
                await regexService.fetchRegexData(accessToken);
                setRegexLoaded(true);
            } catch (error) {
                console.error("Failed to load regex patterns", error);
            }
        };

        if (accessToken) {
            loadRegexPatterns();
        }
    }, [accessToken]);

    const handleExport = async () => {
        if (!data.length) {
            message.error("The grid is currently empty. There are no records to export.");
            return;
        }
        const requestData = {
            pageNo: 1,
            pageSize: -1,
            search: JSON.stringify(searchFilters),
            orderByColumn: "CreatedOn",
            orderBy: "DESC",
        };

        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetMRBQueueDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(requestData),
            });
            const responseText = await response.text();
            let result = [];
            if (!response.ok) {
                if (responseText?.includes("No books found.")) {
                    message.error("The grid is currently empty. There are no records to export.");
                    setData([]);
                    setPagination((prev) => ({
                        ...prev,
                        current: 1,
                        total: 0,
                    }));
                    return;
                } else {
                    throw new Error(responseText || "Network response was not ok");
                }
            }

            try {
                result = JSON.parse(responseText);
                result = responseText.includes("No books found.") ? [] : result;
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }

            const books = Array.isArray(result) ? result : [result];

            if (books.length === 0) {
                message.error("The grid is currently empty. There are no records to export.");
                return;
            }

            const newArray = books.map((item) => {
                return {
                    ...item,
                    inReview: item.inReview === true ? "In Review" : "",
                };
            });

            const updatedColumn = columns.filter(
                (person) => person.key !== "actions",
            );
            const dynamicFileName = buildTimestampedFileName("MRB_Queue");
            exportToExcel(newArray, updatedColumn, dynamicFileName);
        } catch (err) {
            console.error(err);
            message.error("Failed to export data. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    const handleActionSelect = (action) => {
        setSelectedAction(action);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {};
        if (!selectedAction) {
            newErrors.action = "Action is required.";
        }
        if ((!selectedOpeId || selectedOpeId?.length === 0)) {
            newErrors.vendor = "Vendor is required for this action.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let formValues;
        try {
            formValues = await form.validateFields();
        } catch (error) {
            return;
        }

        setErrors({});

        setIsDisabled(true);
        setTimeout(() => {
            setIsDisabled(false);
        }, 2000);

        const payload = {
            bid: selectedRecord?.bid || "",
            isbn: selectedRecord?.isbn || "",
            status: selectedAction,
            userId: roleUserId,
            vendorId: selectedOpeId
                ? parseInt(selectedOpeId, 10)
                : selectedRecord?.vendorId ?? null,
            vendorRackandShelf: formValues.rackAndShelf || rackAndShelfVal,
        };

        setLoading(true);

        try {
            const response = await fetch(
                CommonObj.Endpoints.SubmitMRBQueue,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                        "X-CSRF-Token": encryptTimestamp(),
                    },
                    body: JSON.stringify(payload),
                },
            );

            if (!response.ok) {
                throw new Error("Error submitting action status");
            }

            const selectedStatusObj = exceptionActionStatuses.find(
                (status) => status.statusID === selectedAction,
            );

            const statusName = selectedStatusObj
                ? selectedStatusObj.statusName
                : "selected action";

            let successMessage = "";

            if (statusName.toLowerCase().startsWith("move to")) {
                const cleanName = statusName.replace(/^Move to\s+/i, "");
                successMessage = `Book successfully moved to ${cleanName} stage.`;
            } else {
                successMessage = `Book successfully moved to ${statusName} stage.`;
            }

            message.success(successMessage);
            handleDrawerClose();
            fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
        } catch (error) {
            console.error("Error:", error.message);
            message.error("Failed to submit action status.");
        } finally {
            setLoading(false);
        }
    };

    const fetchExceptionActionStatuses = async () => {
        try {
            const response = await fetch(
                `${CommonObj.Endpoints.GetExceptionActionStatus}?isMRBQISBN=${true}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "X-CSRF-Token": encryptTimestamp(),
                        "Content-Type": "application/json",
                    },
                },
            );

            if (!response.ok) {
                throw new Error("Failed to fetch action statuses");
            }

            const data = await response.json();
            setExceptionActionStatuses(data);
        } catch (error) {
            console.error("Error:", error.message);
            message.error("Unable to fetch action statuses.");
        }
    };
    function cusfun(e) {
        e.preventDefault();
    }

    const handleVendorChange = (e) => {
        setSelectedOpeId(e);
    };

    const validateRackShelf = (_, value) => {
        if (!value) {
            return Promise.reject(new Error("Rack and Shelf is required."));
        }

        if (
            value &&
            selectedRecord?.rackandShelf &&
            removeSpaces(value).toLowerCase() === removeSpaces(selectedRecord.rackandShelf).toLowerCase()
        ) {
            return Promise.reject(
                new Error(
                    "The Book cannot be placed back in the same rack. Please scan a new rack and shelf."
                )
            );
        }

        if (value && regexLoaded) {
            const validation = regexService.validateRackShelf(value.trim());
            if (!validation.isValid) {
                return Promise.reject(
                    new Error("Input format invalid: No matching pattern found.")
                );
            }
        }

        return Promise.resolve();
    };
    if (loading) {
        return <Spin size="large" style={{ margin: "20px auto" }} />;
    }

    return (
        <>
            <Modal
                title={`Scan ${activeScanner ? activeScanner : ""}`}
                open={!!activeScanner}
                onCancel={() => setActiveScanner(null)}
                footer={null}
                destroyOnClose
                className="scan-model-container"
            >
                <div className="scan-modal">
                    <Html5QrcodePlugin
                        fps={20}
                        qrbox={500}
                        disableFlip={false}
                        qrCodeSuccessCallback={onNewScanResult}
                        supportedScanTypes={[Html5QrcodeScanType.SCAN_TYPE_CAMERA]}
                    />
                </div>
            </Modal>
            <Drawer
                className="story-slider"
                title="MRB Queue Action"
                placement="right"
                onClose={handleDrawerClose}
                open={drawerVisible}
                width={540}
                footer={
                    <div
                        className="exception book-page-action-container"
                        style={{ justifyContent: "end" }}
                    >
                        <div style={{ textAlign: "right" }}>
                            <div className="book-page-buttons-container justify-space">
                                <button
                                    type="button"
                                    className="cancel-button-gray-outline"
                                    onClick={() => {
                                        handleDrawerClose();
                                    }}
                                >
                                    <span>Cancel</span>
                                </button>
                                <button
                                    className="submit-button-red-outline"
                                    data-track="MRBQ-action-submit"
                                    disabled={isDisabled}
                                    onClick={handleSubmit}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                }
            >
                <Form form={form} layout="vertical">
                    <div>
                        <div className="field-group" style={{ marginBottom: "24px" }}>
                            <Select
                                style={{
                                    width: "100%",
                                    height: "40px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "6px",
                                }}
                                onChange={(val) => {
                                    handleActionSelect(val);
                                    setErrors((prev) => ({ ...prev, action: null, vendor: null }));
                                }}
                                optionLabelProp="label"
                                placeholder="Select Action *"
                                value={selectedAction}
                            >
                                <Option
                                    disabled
                                    label={
                                        <span>
                                            Select Action <span style={{ color: "red" }}>*</span>
                                        </span>
                                    }
                                >
                                    <span style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginLeft: 8 }}>
                                            Select Action <span style={{ color: "red" }}>*</span>
                                        </span>
                                    </span>
                                </Option>
                                {exceptionActionStatuses.map((status) => (
                                    <Option
                                        key={status.statusID}
                                        value={status.statusID}
                                        label={status.statusName}
                                    >
                                        <span style={{ display: "flex", alignItems: "center" }}>
                                            {status.statusID === selectedAction ? (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2Z"
                                                        stroke="url(#paint0_linear_5157_22877)"
                                                        strokeWidth="4"
                                                    />
                                                    <defs>
                                                        <linearGradient
                                                            id="paint0_linear_5157_22877"
                                                            x1="16.48"
                                                            y1="7.9558"
                                                            x2="-0.0557203"
                                                            y2="9.67356"
                                                            gradientUnits="userSpaceOnUse"
                                                        >
                                                            <stop stopColor="#033D9B" />
                                                            <stop offset="1" stopColor="#29ACD5" />
                                                        </linearGradient>
                                                    </defs>
                                                </svg>
                                            ) : (
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M8 0.5C12.1421 0.5 15.5 3.85786 15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5Z"
                                                        stroke="#CCDAE4"
                                                    />
                                                </svg>
                                            )}
                                            <span style={{ marginLeft: 8 }}>{status.statusName}</span>
                                        </span>
                                    </Option>
                                ))}
                            </Select>
                            {errors.action && (
                                <span className="error-msg">{errors.action}</span>
                            )}
                        </div>

                        <Form.Item
                            label="Vendor"
                            required
                            className="rejection-reason-slider"
                            validateStatus={errors.vendor ? "error" : ""}
                            help={errors.vendor}
                            style={{ marginBottom: "24px" }}
                        >
                            <Select
                                id="operator-select"
                                className={`book-page-form-input-text ${selectedOpeId ? "has-text" : ""}`}
                                showSearch
                                allowClear
                                bordered={false}
                                value={selectedOpeId}
                                placeholder="Select a Vendor"
                                onChange={(value) => {
                                    handleVendorChange(value);
                                    setErrors((prev) => ({ ...prev, vendor: null }));
                                }}
                                style={{ width: "100%", height: "40px", border: "1px solid #d9d9d9" }}
                                filterOption={(input, option) =>
                                    (option?.children ?? "")
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            >
                                {operatorOptions?.map((option) => (
                                    <Option
                                        key={option.vendorID}
                                        value={String(option.vendorID)}
                                    >
                                        {option.vendorName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <div style={{ marginBottom: "16px" }}>
                            <label className="form-label">
                                Scan Rack Shelf<span className="form-required-rack">*</span>
                            </label>

                            <div className="book-page-form-scan-rack-container modal-scan-rack bgColor span2column">
                                <Form.Item
                                    name="rackAndShelf"
                                    rules={[
                                        {
                                            validator: validateRackShelf,
                                        },
                                    ]}
                                    className="book-page-form-scan-rack-button"
                                >
                                    <Input
                                        placeholder="Enter / Scan Rack Shelf Barcode"
                                        data-track="MRBQ_Action-actionSlide-enter-rack"
                                    />
                                </Form.Item>

                                <button
                                    className="book-page-form-scan-rack-button-icon"
                                    htmlType="submit"
                                    data-track="MRBQ_Action-actionSlide-open-rack-scanner"
                                    onClick={(event) => {
                                        setDecodedResults([]);
                                        setActiveScanner("Rack & Shelf ID");
                                        cusfun(event);
                                    }}
                                >
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="12"
                                            cy="13"
                                            r="3"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M10.0001 20H14.0001C16.809 20 18.2135 20 19.2224 19.3259C19.6591 19.0341 20.0341 18.6591 20.326 18.2223C21.0001 17.2134 21.0001 15.8089 21.0001 13C21.0001 10.1911 21 8.78685 20.3258 7.77795C20.034 7.34119 19.659 6.96619 19.2222 6.67436C18.2133 6.00023 16.8089 6.00023 14 6.00023H9.99995C7.19103 6.00023 5.78656 6.00023 4.77767 6.67436C4.34091 6.96619 3.96591 7.34119 3.67407 7.77795C3.0001 8.78663 3.0001 10.1905 3.0001 12.9982L3.0001 13C3.0001 15.8089 3.0001 17.2134 3.67422 18.2223C3.96605 18.6591 4.34106 19.0341 4.77782 19.3259C5.78671 20 7.19117 20 10.0001 20Z"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            d="M18 10H17.5"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M14.5 3.5H9.5"
                                            stroke="#1C274C"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <svg
                                        width="20"
                                        height="18"
                                        viewBox="0 0 20 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M6.00098 1C6.00098 0.447715 6.41425 0 6.92405 0H13.0779C13.5877 0 14.001 0.447715 14.001 1C14.001 1.55228 13.5877 2 13.0779 2H6.92405C6.41425 2 6.00098 1.55228 6.00098 1Z"
                                            fill="url(#paint0_linear_2146_40101)"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.77875 18H12.2232C15.3442 18 16.9047 18 18.0257 17.2777C18.511 16.965 18.9277 16.5632 19.252 16.0953C20.001 15.0143 20.001 13.5095 20.001 10.4999C20.001 7.49027 20.0008 5.9857 19.2518 4.90473C18.9275 4.43676 18.5109 4.03497 18.0256 3.72228C16.9046 3 15.3441 3 12.223 3H7.77859C4.65756 3 3.09705 3 1.97605 3.72228C1.49077 4.03497 1.0741 4.43676 0.749835 4.90473C0.000976503 5.98547 0.000976526 7.48961 0.000976563 10.4979L0.000976563 10.4999C0.000976563 13.5095 0.000976622 15.0143 0.750001 16.0953C1.07426 16.5632 1.49093 16.965 1.97622 17.2777C3.09721 18 4.65773 18 7.77875 18ZM5.83431 10.4999C5.83431 8.28085 7.69979 6.48196 10.001 6.48196C12.3022 6.48196 14.1676 8.28085 14.1676 10.4999C14.1676 12.7189 12.3022 14.5178 10.001 14.5178C7.69979 14.5178 5.83431 12.7189 5.83431 10.4999ZM7.50098 10.4999C7.50098 9.16846 8.62027 8.08913 10.001 8.08913C11.3817 8.08913 12.501 9.16846 12.501 10.4999C12.501 11.8313 11.3817 12.9106 10.001 12.9106C8.62027 12.9106 7.50098 11.8313 7.50098 10.4999ZM16.1121 6.48196C15.6519 6.48196 15.2788 6.84174 15.2788 7.28554C15.2788 7.72935 15.6519 8.08913 16.1121 8.08913H16.6676C17.1279 8.08913 17.501 7.72935 17.501 7.28554C17.501 6.84174 17.1279 6.48196 16.6676 6.48196H16.1121Z"
                                            fill="url(#paint1_linear_2146_40101)"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_2146_40101"
                                                x1="20.601"
                                                y1="8.95028"
                                                x2="-0.0170404"
                                                y2="11.3301"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_2146_40101"
                                                x1="20.601"
                                                y1="8.95028"
                                                x2="-0.0170404"
                                                y2="11.3301"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </button>
                            </div>
                            {errors.rackAndShelf && (
                                <span
                                    className="error-msg"
                                    style={{ color: "red", display: "block", marginTop: "4px" }}
                                >
                                    {errors.rackAndShelf}
                                </span>
                            )}
                        </div>
                    </div>
                </Form>
            </Drawer>

            <div className="active-page-container">
                <div className="active-page-header-container">
                    <div className="active-page-header-title-container">
                        <h4>MRB Queue</h4>
                    </div>
                    <div className="active-page-header-action-container">
                        <div className="active-page-header-button-container">
                            <button
                                className="active-page-header-button"
                                data-track="MRBQ-queue-export"
                                onClick={handleExport}
                            >
                                <span>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_875_15157)">
                                            <path
                                                d="M11.5447 3.04024L11.1433 3.48621L11.1433 3.48621L11.5447 3.04024ZM14.5138 5.71241L14.1124 6.15839L14.5138 5.71241ZM16.2405 7.61557L15.6924 7.85967L15.6924 7.85968L16.2405 7.61557ZM2.37868 15.6213L2.80294 15.1971H2.80294L2.37868 15.6213ZM15.6213 15.6213L15.1971 15.1971L15.6213 15.6213ZM10.5 16.5V15.9H7.5V16.5V17.1H10.5V16.5ZM1.5 10.5H2.1V7.5H1.5H0.9V10.5H1.5ZM16.5 10.1722H15.9V10.5H16.5H17.1V10.1722H16.5ZM11.5447 3.04024L11.1433 3.48621L14.1124 6.15839L14.5138 5.71241L14.9152 5.26643L11.9461 2.59426L11.5447 3.04024ZM16.5 10.1722H17.1C17.1 8.90922 17.1121 8.09787 16.7886 7.37147L16.2405 7.61557L15.6924 7.85968C15.8879 8.29862 15.9 8.80322 15.9 10.1722H16.5ZM14.5138 5.71241L14.1124 6.15839C15.1299 7.07417 15.4969 7.42073 15.6924 7.85967L16.2405 7.61557L16.7886 7.37147C16.4651 6.64507 15.8539 6.11131 14.9152 5.26643L14.5138 5.71241ZM7.52234 1.5V2.1C8.71153 2.1 9.15094 2.10926 9.54196 2.25931L9.75692 1.69914L9.97188 1.13897C9.325 0.890737 8.62066 0.9 7.52234 0.9V1.5ZM11.5447 3.04024L11.9461 2.59426C11.1337 1.8631 10.6187 1.38716 9.97188 1.13897L9.75692 1.69914L9.54196 2.25931C9.93306 2.40939 10.2639 2.69476 11.1433 3.48621L11.5447 3.04024ZM7.5 16.5V15.9C6.06882 15.9 5.0558 15.8987 4.28813 15.7955C3.53772 15.6946 3.11222 15.5063 2.80294 15.1971L2.37868 15.6213L1.95442 16.0456C2.52382 16.615 3.24476 16.866 4.12824 16.9848C4.99445 17.1013 6.10275 17.1 7.5 17.1V16.5ZM1.5 10.5H0.9C0.9 11.8973 0.898726 13.0055 1.01519 13.8718C1.13397 14.7552 1.38501 15.4762 1.95442 16.0456L2.37868 15.6213L2.80294 15.1971C2.49367 14.8878 2.30537 14.4623 2.20448 13.7119C2.10127 12.9442 2.1 11.9312 2.1 10.5H1.5ZM10.5 16.5V17.1C11.8973 17.1 13.0055 17.1013 13.8718 16.9848C14.7552 16.866 15.4762 16.615 16.0456 16.0456L15.6213 15.6213L15.1971 15.1971C14.8878 15.5063 14.4623 15.6946 13.7119 15.7955C12.9442 15.8987 11.9312 15.9 10.5 15.9V16.5ZM16.5 10.5H15.9C15.9 11.9312 15.8987 12.9442 15.7955 13.7119C15.6946 14.4623 15.5063 14.8878 15.1971 15.1971L15.6213 15.6213L16.0456 16.0456C16.615 15.4762 16.866 14.7552 16.9848 13.8718C17.1013 13.0055 17.1 11.8973 17.1 10.5H16.5ZM1.5 7.5H2.1C2.1 6.06882 2.10127 5.0558 2.20448 4.28813C2.30537 3.53772 2.49367 3.11222 2.80294 2.80294L2.37868 2.37868L1.95442 1.95442C1.38501 2.52382 1.13397 3.24476 1.01519 4.12824C0.898726 4.99445 0.9 6.10275 0.9 7.5H1.5ZM7.52234 1.5V0.9C6.11759 0.9 5.0039 0.898736 4.13418 1.01514C3.24765 1.13379 2.52428 1.38455 1.95442 1.95442L2.37868 2.37868L2.80294 2.80294C3.11175 2.49413 3.53856 2.30555 4.29336 2.20453C5.06497 2.10126 6.08378 2.1 7.52234 2.1V1.5Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M9.75 1.875V3.75C9.75 5.51777 9.75 6.40165 10.2992 6.95083C10.8483 7.5 11.7322 7.5 13.5 7.5H16.5"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M6.375 10.125L6.375 13.875M6.375 13.875L7.875 12.4688M6.375 13.875L4.875 12.4688"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_875_15157">
                                                <rect width="18" height="18" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="active-page-body-container">
                    <div className="rounded-table-wrapper">
                        <Table
                            columns={columns}
                            dataSource={data}
                            className="common-pagination common-grid"
                            pagination={{
                                ...pagination,
                                pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                                showSizeChanger: true,
                                position: ["bottomRight"],
                                defaultPageSize: 10,
                                showTotal: (total, range) =>
                                    renderCommonPaginationTotal(total, range, {
                                        pagination,
                                        handleTableChange,
                                        searchFilters,
                                        sortedInfo,
                                    }),
                            }}
                            rowKey={(record) => record.bidNo}
                            scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                            rowClassName={getRowClassName}
                            onChange={handleTableChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

MrbQueue.propTypes = {};

export default MrbQueue;
